<template>
    <div>
        <v-select
            :label="label"
            :items="choices"
            v-model="internal_value"
            return-object
            v-on:input="onChange"
            :rules="select_rules"
            :disabled="disabled"
        />
    </div>
</template>

<script>
const E164 = require('@/js/E164.js');

export default {

    props: {

        organization_id: {
            type: String,
            default: function() { return null; }
        },

        value: {
            type: String,
            default: function() { return null; },
        },

        label: {
            type: String,
            default: function() { return 'Phone Number'; },
        },

        required: {
            type: Boolean,
            default: function() { return false; },
        },

        only_toll_free_numbers: {
            type: Boolean,
            default: function() { return false; },
        },

        disabled: {
            type: Boolean,
            default: function() { return false; },
        },

        include_bot_numbers: {
            type: Boolean,
            default: function() { return false; },
        },
    },

    data() {
        return {
            raw_choices: [],
            internal_value: null,
            rules: {
                required: (value) => {

                    if ( value == null || value.value == null ) return 'Required';

                    return true;
                },
            },
        }
    },

    mounted () {
        this.update();
    },

    methods: {
        update() {

            if ( this.organization_id == null )
            {
                this.raw_choices = [];
                this.internal_value = null;
                return;
            }

            // don't include bot numbers
            let search = {
                'organization_id' : this.organization_id,
                'sort' : 'last_known_lidb_label',
            };

            if ( !this.include_bot_numbers )
            {
                search['assignment'] = {
                    'type' : "!=",
                    'value' : ['assigned-to-organization-bot','available'],
                };
            }

            if ( this.only_toll_free_numbers )
            {
                search.sub_type = ['ptsn-toll-free'];
            }

            this.$rc.rest({
                'spinner': true,
                'endpoint': 'LIST: /api/v1/numbers',

                'params' : search,

                'on_success': (data) => {
                        this.raw_choices = data.results;
                        this.internal_value = this.createInternalValue(this.value);
                    },

                'on_error': 'Unable to list phone numbers',
            });
        },

        createInternalValue(value)
        {
            let choices = this.choices;

            for ( let i = 0; i < choices.length; i++ )
            {
                if ( choices[i].value == value )
                    return choices[i];
            }

            // If the current value is not found in available choices,
            // don't default to the first choice as this can cause
            // messages to send from wrong numbers
            if ( value != null )
            {
                // Return a placeholder choice showing the unavailable number
                return {
                    'text': 'Unavailable: ' + value,
                    'value': value,
                    'unavailable': true
                };
            }

            return choices[0];
        },

        onChange(value)
        {
            this.$emit('input',value.value);
        },
    },

    computed: {
        select_rules() {
            let rules = [];

            if ( this.required == true )
                rules.push(this.rules.required);

            // Add validation to prevent using unavailable numbers
            rules.push((value) => {
                if ( value && value.unavailable )
                    return 'Please select an active phone number';
                return true;
            });

            return rules;
        },

        choices()
        {
            let ret = [];

            if ( this.value == null )
            {
                if ( this.only_toll_free_numbers )
                {
                    ret.push({
                        'text' : 'Select Toll Free Number',
                        'value' : null,
                    });
                }
                else
                {
                    ret.push({
                        'text' : 'Select Phone Number',
                        'value' : null,
                    });
                }
            }

            let phone_numbers = this.raw_choices;

            for ( let i = 0; i < phone_numbers.length; i++ )
            {
                let label = null;

                if ( phone_numbers[i].assignment == 'assigned-to-employee' )
                {
                    label = phone_numbers[i].assigned_to_employee_label;
                }

                if ( phone_numbers[i].assignment == 'assigned-to-employee-group' )
                {
                    label = phone_numbers[i].assigned_to_employee_group_label;
                }

                if ( phone_numbers[i].assignment == 'available' )
                {
                    label = 'Available';
                }

                if ( phone_numbers[i].assignment == 'assigned-to-organization-bot' )
                {
                    label = phone_numbers[i].organization_label + ' Bot';
                }

                if ( !label )
                {
                    label = phone_numbers[i].last_known_lidb_label;
                }

                let text = label + " - " + E164.prettyPrintUsPhoneNumber(phone_numbers[i].endpoint);

                ret.push({
                    'text' : text,
                    'value' : phone_numbers[i].endpoint,
                    'is_bot': phone_numbers[i].assignment == 'assigned-to-organization-bot',
                });
            }

            // If the current value is not in the available choices, add it as unavailable
            if ( this.value != null )
            {
                let found = false;
                for ( let i = 0; i < ret.length; i++ )
                {
                    if ( ret[i].value == this.value )
                    {
                        found = true;
                        break;
                    }
                }

                if ( !found )
                {
                    ret.push({
                        'text': 'Unavailable: ' + this.value,
                        'value': this.value,
                        'unavailable': true
                    });
                }
            }

            return ret;
        },
    },

    watch: {
        organization_id(newValue, oldValue) {
            this.update();
        },

        value(newValue, oldValue) {
            this.internal_value = this.createInternalValue(newValue);
        },

        internal_value: {
            handler(newValue) {
                if (newValue && newValue.unavailable) {
                    this.$emit('validation', { isValid: false, message: 'Please select an active phone number' });
                } else {
                    this.$emit('validation', { isValid: true });
                }
            },
            immediate: true,
            deep: true
        },
    },
}
</script>

<style lang="scss" scoped>

</style>